import DateUtils        from "@/core/utils/DateUtils";
import ApplicationError from "@/core/misc/ApplicationError";
import { ErrorSeverity } from "@/core/misc/ErrorSeverity";

/**
 * Класс для работы с датами без времени
 * Наследует от Date, но обнуляет время и добавляет удобные методы
 */
class DateSimple extends Date {
	/**
	 * Создать новый экземпляр DateSimple
	 * Автоматически обнуляет время (00:00:00.000)
	 *
	 * @param args - Любые параметры, поддерживаемые конструктором Date
	 *
	 * @example
	 * // Без параметров (текущая дата)
	 * const date1 = new DateSimple();
	 * console.log(date1.getHours()); // 0
	 *
	 * @example
	 * // Из строки
	 * const date2 = new DateSimple('2025-03-26');
	 * console.log(date2.toLocalString()); // "26.03.2025"
	 *
	 * @example
	 * // Из другой даты
	 * const date3 = new DateSimple(new Date());
	 * console.log(date3.getHours()); // 0 (время обнулено)
	 *
	 * @example
	 * // Из миллисекунд
	 * const date4 = new DateSimple(1711468800000);
	 * console.log(date4.toLocalString()); // "26.03.2024"
	 *
	 * @example
	 * // Из компонентов (год, месяц, день)
	 * const date5 = new DateSimple(2025, 2, 26); // месяц с 0 (март)
	 * console.log(date5.toLocalString()); // "26.03.2025"
	 *
	 * @throws ApplicationError если аргумент передан, но пуст (null/undefined)
	 */
	constructor(...args: ConstructorParameters<typeof Date>) {
		/**
		 * Явно переданная пустота — дефект вызывающего, а не дата.
		 *
		 * Молча она даёт не отсутствие результата, а правдоподобный неверный:
		 * new Date(null) — это new Date(0), то есть 01.01.1970. Дата выходит
		 * валидная, isNaN её не отсекает, дальше по коду она ведёт себя как
		 * настоящая, и признака ошибки нет нигде. new Date(undefined) даёт
		 * Invalid Date — тоже молча: setHours на ней не бросает, а
		 * toDateString() печатает "NaN-NaN-NaN".
		 *
		 * Отсутствие аргументов — другое дело: new DateSimple() это «сегодня»,
		 * штатный и частый путь. Отличаем одно от другого по args.length, а не
		 * по значению args[0]: rest-параметры сохраняют арность вызова, и
		 * new DateSimple() даёт длину 0, а new DateSimple(undefined) — длину 1.
		 */
		if (args.length > 0 && (args[0] === null || args[0] === undefined)) {
			throw new ApplicationError(
				'DateSimple: попытка создать дату из пустого значения',
				0,
				ErrorSeverity.Error,
				{ value: String(args[0]) },
			);
		}

		if (typeof args[0] === 'string') {
			super(DateSimple.normalizeDateString(args[0]));
		} else {
			super(...args);
		}

		// Обнуляем время
		this.setHours(0, 0, 0, 0);
	}

	/**
	 * Приводит строку даты к виду, который Date разбирает как ЛОКАЛЬНОЕ время.
	 *
	 * По спецификации ECMAScript строка ТОЛЬКО с датой ('2026-07-24')
	 * разбирается как UTC, а строка С временем без указания зоны
	 * ('2026-07-24T00:00:00') — как локальное время. Одна и та же дата,
	 * два разных момента:
	 *
	 *   new Date('2026-07-24')          → 24 июля 00:00 UTC
	 *   new Date('2026-07-24T00:00:00') → 24 июля 00:00 по локальному
	 *
	 * Конструктор обнуляет время через setHours, а он работает с ЛОКАЛЬНЫМИ
	 * часами. Для пользователя в UTC-5 первая строка даёт момент, который
	 * локально выглядит как 19:00 двадцать третьего июля; обнуление часов
	 * оставляет 23 июля. День потерян.
	 *
	 * Поэтому всем распознанным форматам дописывается нулевое время — это
	 * переключает разбор с UTC на локальный. Нераспознанное отдаётся Date
	 * как есть: строки, где время уже указано, и так читаются как локальные.
	 *
	 * Единственное место, знающее о форматах дат: и конструктор, и parse
	 * ходят сюда.
	 *
	 * @param dateString - Строка даты в любом формате
	 * @returns Строка, пригодная для Date, либо исходная без изменений
	 */
	private static normalizeDateString(dateString: string): string {
		// ISO формат YYYY-MM-DD
		if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateString)) {
			const [year, month, day] = dateString.split('-');
			return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00`;
		}
		// Европейский формат DD.MM.YYYY
		else if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString)) {
			const [day, month, year] = dateString.split('.');
			return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00`;
		}
		// Формат DD/MM/YYYY
		else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
			const [day, month, year] = dateString.split('/');
			return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00`;
		}
		// Остальное разбирает сам Date
		else {
			return dateString;
		}
	}

	/**
	 * Создать DateSimple из Date объекта
	 *
	 * @param data - Date объект
	 * @returns DateSimple экземпляр или null
	 *
	 * @example
	 * const ds = DateSimple.fromServer(new Date());
	 * console.log(ds.toDisplayDate()); // "26.03.2025"
	 *
	 * @example
	 * const now = new Date();
	 * const dateSimple = DateSimple.fromServer(now);
	 * // Время обнулено: 00:00:00
	 */
	static fromServer(data: Date): DateSimple | null;
	/**
	 * Создать DateSimple из строки даты
	 *
	 * @param data - Строка в формате YYYY-MM-DD или другом валидном формате
	 * @returns DateSimple экземпляр или null
	 *
	 * @example
	 * const ds = DateSimple.fromServer("2025-03-26");
	 * console.log(ds.toDisplayDate()); // "26.03.2025"
	 *
	 * @example
	 * const ds = DateSimple.fromServer("2025-03-26T20:51:19");
	 * // Время игнорируется
	 */
	static fromServer(data: string): DateSimple | null;
	/**
	 * Создать DateSimple из объекта бэкенда
	 *
	 * @param data - Объект с полем date
	 * @returns DateSimple экземпляр или null
	 *
	 * @example
	 * const ds = DateSimple.fromServer({
	 *   date: "2025-03-26"
	 * });
	 * console.log(ds.toDisplayDate()); // "26.03.2025"
	 *
	 * @example
	 * // С полной датой и временем (время игнорируется)
	 * const ds = DateSimple.fromServer({
	 *   date: "2025-03-26 20:51:19.818168"
	 * });
	 * console.log(ds.toDisplayDate()); // "26.03.2025"
	 */
	static fromServer(data: { date: string }): DateSimple | null;
	/** Реализация **/
	static fromServer(data: Date | string | { date: string } | null): DateSimple | null {
		if (!data) { return null }

		let dateString: string;

		// Date объект
		if (data instanceof Date) {
			const year = data.getFullYear();
			const month = String(data.getMonth() + 1).padStart(2, '0');
			const day = String(data.getDate()).padStart(2, '0');
			dateString = `${year}-${month}-${day}`;
		}

		// Строка
		else if (typeof data === 'string') {
			dateString = data.split('T')[0]; // Берём только дату до T
		}
		// Объект бэкенда
		else if (typeof data === 'object' && 'date' in data) {
			dateString = data.date.split(' ')[0]; // Берём только дату до пробела
		}
		else {
			return null;
		}

		const dateSimple = new DateSimple(dateString);


		// Обнуляем время
		dateSimple.setHours(0, 0, 0, 0);

		return isNaN(dateSimple.getTime()) ? null : dateSimple;
	}


	/**
	 * Проверяет, является ли значение экземпляром DateSimple
	 *
	 * @param value - Проверяемое значение
	 * @returns true если значение является DateSimple
	 *
	 * @example
	 * DateSimple.isDateSimple(new DateSimple()); // true
	 * DateSimple.isDateSimple(new Date()); // false
	 * DateSimple.isDateSimple("2025-03-26"); // false
	 */
	static isDateSimple(value: any): value is DateSimple {
		return value instanceof DateSimple
	}


	/**
	 * Проверяет, является ли значение валидным экземпляром Date
	 *
	 * @param value - Проверяемое значение
	 * @returns true если значение является валидным Date
	 *
	 * @example
	 * DateSimple.isDate(new Date()); // true
	 * DateSimple.isDate(new DateSimple()); // true
	 * DateSimple.isDate(new Date('invalid')); // false
	 * DateSimple.isDate("2025-03-26"); // false
	 */
	static isDate(value: any): value is Date {
		return value instanceof Date && !isNaN(value.getTime());
	}

	/**
	 * Преобразует дату к началу дня (00:00:00.000)
	 *
	 * @param date - Исходная дата
	 * @returns Новый объект DateSimple с обнуленным временем
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * const dayStart = DateSimple.toDayStart(date);
	 * console.log(dayStart.getHours()); // 0
	 */
	static toDayStart(date: DateSimple): DateSimple {
		if (!DateSimple.isDateSimple(date)) {
			throw new TypeError('Неверный тип данных');
		}
		return new DateSimple(date);
	}

	/**
	 * Создаёт копию даты
	 *
	 * @returns Новый объект DateSimple с той же датой
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * const copy = date.clone();
	 * copy.setDate(1); // не затронет оригинал
	 */
	// @ts-ignore — Date принимает Date объект, но TS не знает об этом
	clone(): DateSimple {
		return new DateSimple(this);
	}

	/**
	 * Добавляет дни к дате
	 *
	 * @param days - Количество дней (может быть отрицательным)
	 * @returns Новый объект DateSimple
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * date.addDays(5); // 2025-03-31
	 * date.addDays(-5); // 2025-03-21
	 */
	addDays(days: number): DateSimple {
		const newDate = new DateSimple(this);
		newDate.setDate(newDate.getDate() + days);
		return newDate;
	}

	/**
	 * Добавляет месяцы к дате
	 *
	 * @param months - Количество месяцев (может быть отрицательным)
	 * @returns Новый объект DateSimple
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * date.addMonths(2); // 2025-05-26
	 * date.addMonths(-1); // 2025-02-26
	 */
	addMonths(months: number): DateSimple {
		const newDate = new DateSimple(this);
		newDate.setMonth(newDate.getMonth() + months);
		return newDate;
	}

	/**
	 * Добавляет годы к дате
	 *
	 * @param years - Количество лет (может быть отрицательным)
	 * @returns Новый объект DateSimple
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * date.addYears(1); // 2026-03-26
	 * date.addYears(-2); // 2023-03-26
	 */
	addYears(years: number): DateSimple {
		const newDate = new DateSimple(this);
		newDate.setFullYear(newDate.getFullYear() + years);
		return newDate;
	}

	/**
	 * Добавляет время к дате (не мутирует исходную дату)
	 *
	 * @param options - Опции добавления
	 * @param options.days - Количество дней
	 * @param options.months - Количество месяцев
	 * @param options.years - Количество лет
	 * @returns Новый объект DateSimple
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * date.add({ days: 5, months: 2 }); // 2025-05-31
	 *
	 * @example
	 * date.add({ years: 1 }); // 2026-03-26
	 *
	 * @example
	 * date.add({ days: -5, months: 1 }); // 2025-04-21
	 */
	add({
			days = 0,
			months = 0,
			years = 0,
		}: {
		days?: number;
		months?: number;
		years?: number;
	} = {}): DateSimple {
		let result = new DateSimple(this);

		if (days && Number.isInteger(days)) {
			result = result.addDays(days);
		}

		if (months && Number.isInteger(months)) {
			result = result.addMonths(months);
		}

		if (years && Number.isInteger(years)) {
			result = result.addYears(years);
		}

		return result;
	}

	/**
	 * Вычитает время из текущей даты
	 *
	 * @param options - Опции вычитания
	 * @param options.days - Количество дней
	 * @param options.months - Количество месяцев
	 * @param options.years - Количество лет
	 * @returns Новый объект DateSimple
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * date.subtract({ days: 5 }); // 2025-03-21
	 *
	 * @example
	 * date.subtract({ months: 2, years: 1 }); // 2024-01-26
	 */
	subtract({
				 days = 0,
				 months = 0,
				 years = 0,
			 }: {
		days?: number;
		months?: number;
		years?: number;
	} = {}): DateSimple {
		return this.add({
			days: -days,
			months: -months,
			years: -years,
		});
	}

	/**
	 * Сравнивает даты (игнорирует время)
	 *
	 * @param other - Другая дата
	 * @returns -1 если раньше, 0 если равны, 1 если позже
	 *
	 * @example
	 * const date1 = new DateSimple('2025-03-26');
	 * const date2 = new DateSimple('2025-03-27');
	 * date1.compareTo(date2); // -1
	 * date2.compareTo(date1); // 1
	 * date1.compareTo(date1); // 0
	 */
	compareTo(other: DateSimple | Date): number {
		const thisTime = new DateSimple(this).getTime();
		const otherTime = new DateSimple(other).getTime();

		if (thisTime < otherTime) return -1;
		if (thisTime > otherTime) return 1;
		return 0;
	}

	/**
	 * Проверяет равенство дат
	 *
	 * @param other - Другая дата
	 * @returns true если даты равны
	 *
	 * @example
	 * const date1 = new DateSimple('2025-03-26');
	 * const date2 = new DateSimple('2025-03-26');
	 * date1.equals(date2); // true
	 */
	equals(other: DateSimple | Date): boolean {
		return this.compareTo(other) === 0;
	}

	/**
	 * Проверяет, позже ли текущая дата
	 *
	 * @param other - Другая дата
	 * @returns true если текущая дата позже
	 *
	 * @example
	 * const date1 = new DateSimple('2025-03-27');
	 * const date2 = new DateSimple('2025-03-26');
	 * date1.isAfter(date2); // true
	 */
	isAfter(other: DateSimple | Date): boolean {
		return this.compareTo(other) > 0;
	}

	/**
	 * Проверяет, раньше ли текущая дата
	 *
	 * @param other - Другая дата
	 * @returns true если текущая дата раньше
	 *
	 * @example
	 * const date1 = new DateSimple('2025-03-26');
	 * const date2 = new DateSimple('2025-03-27');
	 * date1.isBefore(date2); // true
	 */
	isBefore(other: DateSimple | Date): boolean {
		return this.compareTo(other) < 0;
	}

	/**
	 * Возвращает разность в днях
	 *
	 * @param other - Другая дата
	 * @returns Количество дней (может быть отрицательным)
	 *
	 * @example
	 * const date1 = new DateSimple('2025-03-26');
	 * const date2 = new DateSimple('2025-03-31');
	 * date1.diffInDays(date2); // 5
	 * date2.diffInDays(date1); // -5
	 */
	diffInDays(other: DateSimple | Date): number {
		if (!DateSimple.isDateSimple(other)) {
			throw new TypeError('Неверный тип данных');
		}

		const thisTime = new DateSimple(this).getTime();
		const otherTime = new DateSimple(other).getTime();
		return Math.floor((thisTime - otherTime) / (1000 * 60 * 60 * 24));
	}

	/**
	 * Возвращает разность в миллисекундах между датами
	 *
	 * @param other - Другая дата
	 * @returns Количество миллисекунд
	 *
	 * @example
	 * const date1 = new DateSimple('2025-03-26');
	 * const date2 = new DateSimple('2025-03-25');
	 * date1.subtractDate(date2); // 86400000 (1 день в мс)
	 */
	subtractDate(other: DateSimple): number {
		if (!DateSimple.isDateSimple(other)) {
			throw new TypeError('Неверный тип данных');
		}

		const otherTime = DateSimple.toDayStart(other).getTime();
		return this.getTime() - otherTime;
	}

	/**
	 * Возвращает дату в формате YYYY-MM-DD
	 *
	 * @returns Строка в формате YYYY-MM-DD
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * date.toDateString(); // "2025-03-26"
	 */
	toDateString(): string {
		const year = this.getFullYear();
		const month = String(this.getMonth() + 1).padStart(2, '0');
		const day = String(this.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	/**
	 * Возвращает дату в локальном формате DD.MM.YYYY
	 *
	 * @returns Строка в формате DD.MM.YYYY
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * date.toLocalString(); // "26.03.2025"
	 */
	toLocalString(): string {
		const year = this.getFullYear();
		const month = String(this.getMonth() + 1).padStart(2, '0');
		const day = String(this.getDate()).padStart(2, '0');
		return `${day}.${month}.${year}`;
	}


	format(format: string = '') {
		return DateUtils.format(this, format);
	}

	/**
	 * Переопределяем toString для вывода только даты
	 *
	 * @returns Строка в формате DD.MM.YYYY
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * date.toString(); // "26.03.2025"
	 * String(date); // "26.03.2025"
	 */
	toString(): string {
		return this.toLocalString();
	}

	/**
	 * Переопределяем toJSON для сериализации только даты
	 *
	 * @returns Строка в формате YYYY-MM-DD
	 *
	 * @example
	 * const date = new DateSimple('2025-03-26');
	 * JSON.stringify({ date }); // {"date":"2025-03-26"}
	 */
	toJSON(): string {
		return this.toDateString();
	}

	/**
	 * Создает DateSimple из текущей даты
	 *
	 * @returns Новый экземпляр DateSimple с текущей датой
	 *
	 * @example
	 * const today = DateSimple.today();
	 * console.log(today.toLocalString()); // "26.03.2025"
	 */
	static today(): DateSimple {
		return new DateSimple();
	}

	/**
	 * Парсит строку даты в различных форматах.
	 *
	 * Отличается от конструктора только строгостью: разбор форматов один и
	 * тот же (normalizeDateString), но конструктор на непарсимой строке даёт
	 * Invalid Date, как обычный Date, а parse бросает исключение.
	 *
	 * @param dateString - Строка даты
	 * @returns Новый экземпляр DateSimple
	 * @throws Error если строку невозможно распарсить
	 *
	 * @example
	 * // ISO формат
	 * DateSimple.parse("2025-03-26"); // DateSimple
	 *
	 * @example
	 * // Европейский формат
	 * DateSimple.parse("26.03.2025"); // DateSimple
	 *
	 * @example
	 * // Формат с слэшами
	 * DateSimple.parse("26/03/2025"); // DateSimple
	 *
	 * @example
	 * // Невалидная строка
	 * try {
	 *   DateSimple.parse("invalid-date");
	 * } catch (error) {
	 *   console.error(error); // "Unable to parse date: invalid-date"
	 * }
	 */
	static parse(dateString: string): DateSimple {
		const result = new DateSimple(dateString);

		if (isNaN(result.getTime())) {
			throw new Error(`Unable to parse date: ${dateString}`);
		}

		return result;
	}
}

export default DateSimple;